const selector = 'img[data-deferred-image]';
const desktopViewport = window.matchMedia('(min-width: 990px)');
let pageImageObserver;
let viewportImageObserver;
let pageWrapper;

function prepareFrame(image) {
  const frame = image.parentElement;
  if (!frame || frame.hasAttribute('data-deferred-image-frame')) return;

  const hasOtherContent = [...frame.children].some(
    (child) => child !== image && !['NOSCRIPT', 'SOURCE'].includes(child.tagName)
  );
  if (hasOtherContent) return;

  frame.setAttribute('data-deferred-image-frame', '');
  frame.dataset.deferredFrameState = image.dataset.deferredState;
}

function markComplete(image, state) {
  image.dataset.deferredState = state;
  const frame = image.parentElement;
  if (frame?.hasAttribute('data-deferred-image-frame')) {
    frame.dataset.deferredFrameState = state;
  }
}

function finishDeferredImage(image, state) {
  if (image.dataset.deferredState === 'loaded' || image.dataset.deferredState === 'error') return;
  markComplete(image, state);
}

// Safari does not support sizes="auto". An unresolved auto value can make the
// selected srcset candidate report a natural width of 0, so the bitmap never paints.
function replaceAutoSizes(image, measuredWidth) {
  const sizes = image.getAttribute('sizes');
  if (!sizes || !/(^|[,\s])auto(?=$|[,\s])/.test(sizes)) return;

  const width = Math.ceil(measuredWidth);
  image.sizes = width > 0 ? `${width}px` : '100vw';
}

function activateImage(image, measuredWidth) {
  if (image.dataset.deferredState !== 'pending') return;

  image.dataset.deferredState = 'loading';
  // Keep WebKit's native lazy loader off this element. It drops a later src
  // change when the current source is still the data-URI placeholder.
  image.loading = 'eager';
  replaceAutoSizes(image, measuredWidth);

  const revealIfReady = () => {
    if (image.complete && image.naturalWidth > 1) finishDeferredImage(image, 'loaded');
  };

  image.addEventListener('load', revealIfReady);
  image.addEventListener('error', () => finishDeferredImage(image, 'error'), { once: true });

  const picture = image.closest('picture');
  picture?.querySelectorAll('source[data-srcset]').forEach((source) => {
    source.srcset = source.dataset.srcset;
    source.removeAttribute('data-srcset');
  });

  const src = image.dataset.src;
  const srcset = image.dataset.srcset;

  // Assign src before srcset. Safari ignores the real URL when srcset is
  // written first against the placeholder, and it can fire load synchronously.
  image.removeAttribute('srcset');
  if (src) {
    image.src = src;
    image.removeAttribute('data-src');
  }
  if (srcset) {
    image.srcset = srcset;
    image.removeAttribute('data-srcset');
  }

  revealIfReady();
  queueMicrotask(revealIfReady);

  if (typeof image.decode === 'function') {
    image.decode().then(revealIfReady).catch(() => {});
  }
}

function isInHiddenSlide(image) {
  const hiddenSlide = image.closest('slideshow-slide[aria-hidden="true"]');

  // Resource-list carousels can show part of the next card while it is still
  // marked aria-hidden. IntersectionObserver determines whether that card is
  // actually visible; keep the hidden-slide guard for alternate gallery media.
  return hiddenSlide !== null && !hiddenSlide.classList.contains('resource-list__slide');
}

function createImageObserver() {
  pageImageObserver?.disconnect();
  viewportImageObserver?.disconnect();

  if (!('IntersectionObserver' in window)) {
    pageImageObserver = null;
    viewportImageObserver = null;
    return;
  }

  pageWrapper = desktopViewport.matches ? document.querySelector('.page-wrapper') : null;
  const handleIntersections = (entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || isInHiddenSlide(entry.target)) return;
      observer.unobserve(entry.target);
      activateImage(entry.target, entry.boundingClientRect.width);
    });
  };

  viewportImageObserver = new IntersectionObserver(
    handleIntersections,
    { root: null, rootMargin: '300px 0px', threshold: 0 }
  );

  pageImageObserver = pageWrapper ? new IntersectionObserver(
    (entries) => {
      handleIntersections(entries, pageImageObserver);
    },
    { root: pageWrapper, rootMargin: '300px 0px', threshold: 0 }
  ) : null;
}

function observerFor(image) {
  // Modal dialogs render in the top layer, outside the page wrapper's clipping root.
  if (image.closest('dialog')) return viewportImageObserver;
  return pageImageObserver && pageWrapper?.contains(image) ? pageImageObserver : viewportImageObserver;
}

function scrollRootFor(image) {
  if (image.closest('dialog')) return null;
  return pageImageObserver && pageWrapper?.contains(image) ? pageWrapper : null;
}

// Safari often skips the first IntersectionObserver callback for elements that
// are already visible inside an overflow root. Measure those directly.
function isNearScrollRoot(rect, bounds) {
  if (rect.width === 0 && rect.height === 0) return false;

  const margin = 300;
  return rect.bottom >= bounds.top - margin
    && rect.top <= bounds.bottom + margin
    && rect.right >= bounds.left
    && rect.left <= bounds.right;
}

function observeImages(root = document) {
  const images = new Set();
  const roots = Array.isArray(root) ? root : [root];
  roots.forEach((candidate) => {
    if (candidate instanceof Element && candidate.matches(selector)) images.add(candidate);
    candidate.querySelectorAll(selector).forEach((image) => images.add(image));
  });

  // Measure the entire batch before frame/source updates invalidate layout.
  const bounds = new Map();
  const pending = [...images].filter((image) => image.dataset.deferredState === 'pending').map((image) => {
    const observer = observerFor(image);
    const root = scrollRootFor(image);
    if (!bounds.has(root)) {
      bounds.set(root, root ? root.getBoundingClientRect()
        : { top: 0, right: window.innerWidth, bottom: window.innerHeight, left: 0 });
    }
    const rect = image.getBoundingClientRect();
    return { image, observer, width: rect.width,
      activate: !observer || (!isInHiddenSlide(image) && isNearScrollRoot(rect, bounds.get(root))) };
  });

  pending.forEach(({ image, observer, width, activate }) => {
    prepareFrame(image);
    pageImageObserver?.unobserve(image);
    viewportImageObserver?.unobserve(image);
    if (activate) {
      activateImage(image, width);
      return;
    }
    observer.observe(image);
  });
}

createImageObserver();
observeImages();

new MutationObserver((mutations) => {
  const roots = new Set();
  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes') {
      roots.add(mutation.target);
      return;
    }

    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) roots.add(node);
    });
  });
  if (roots.size) observeImages([...roots]);
}).observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  // Section rendering can morph an existing image back into a deferred state
  // without adding a new DOM node. Observe those source/state changes so the
  // replacement image is registered with the viewport observer again.
  attributeFilter: ['aria-hidden', 'data-deferred-image', 'data-deferred-state', 'data-src', 'data-srcset'],
});

desktopViewport.addEventListener('change', () => {
  createImageObserver();
  observeImages();
});

document.addEventListener('shopify:section:load', (event) => observeImages(event.target));
